export interface EarlyAccessLead {
  name: string;
  email: string;
  company: string;
  revenue: string;
}

export interface EarlyAccessSubmissionResult {
  submissionId?: string;
  submittedVia: 'siteai_forms' | 'marketing_subscribe';
}

const DEFAULT_FORMS_GATEWAY_URL = 'https://edge.vibecontrol.app';
const DEFAULT_FORM_KEY = 'roleforge-early-access';
const VISITOR_ID_KEY = 'roleforge.visitor_id';
const SESSION_ID_KEY = 'roleforge.session_id';

export async function submitEarlyAccessLead(
  lead: EarlyAccessLead,
): Promise<EarlyAccessSubmissionResult> {
  const formsSiteId = import.meta.env.VITE_ROLEFORGE_FORMS_SITE_ID?.trim();
  const formsGatewayUrl = (
    import.meta.env.VITE_ROLEFORGE_FORMS_GATEWAY_URL || DEFAULT_FORMS_GATEWAY_URL
  ).replace(/\/+$/, '');
  const formKey = import.meta.env.VITE_ROLEFORGE_EARLY_ACCESS_FORM_KEY || DEFAULT_FORM_KEY;
  const attribution = getAttribution();
  const { firstName, lastName } = splitName(lead.name);
  const idempotencyKey = createId();
  const visitorId = getOrCreateStoredId('local', VISITOR_ID_KEY);
  const sessionId = getOrCreateStoredId('session', SESSION_ID_KEY);

  if (formsSiteId) {
    try {
      const response = await fetch(`${formsGatewayUrl}/submit/${formsSiteId}/${formKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: lead.name,
            first_name: firstName,
            last_name: lastName,
            email: lead.email,
            company: lead.company,
            monthly_revenue: lead.revenue,
            source_page: window.location.pathname,
          },
          idempotency_key: idempotencyKey,
          visitor_id: visitorId,
          session_id: sessionId,
          referrer: document.referrer || undefined,
          utm: attribution,
        }),
      });

      if (response.ok) {
        const result = (await response.json().catch(() => ({}))) as { submission_id?: string };
        return { submissionId: result.submission_id, submittedVia: 'siteai_forms' };
      }
    } catch {
      // Fall through to the server-side VibeMailer capture fallback.
    }
  }

  const fallbackResponse = await fetch('/api/early-access', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...lead,
      first_name: firstName,
      last_name: lastName,
      idempotency_key: idempotencyKey,
      visitor_id: visitorId,
      session_id: sessionId,
      referrer: document.referrer || undefined,
      utm: attribution,
    }),
  });

  if (!fallbackResponse.ok) {
    throw new Error('early_access_submission_failed');
  }

  return { submittedVia: 'marketing_subscribe' };
}

function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1] || '',
  };
}

function getAttribution(): Record<string, string | undefined> {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    content: params.get('utm_content') || undefined,
    term: params.get('utm_term') || undefined,
  };
}

function getOrCreateStoredId(storageType: 'local' | 'session', key: string): string {
  const storage = storageType === 'local' ? window.localStorage : window.sessionStorage;
  const existing = storage.getItem(key);
  if (existing) return existing;

  const next = createId();
  storage.setItem(key, next);
  return next;
}

function createId(): string {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
